import React from 'react';
import { LucideIcon } from 'lucide-react';
import { componentThemes } from '../../../design-system/theme';

/**
 * Button variant types
 */
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'glass';

/**
 * Button size types
 */
type ButtonSize = 'small' | 'medium' | 'large';

/**
 * Props interface for the reusable Button component
 */
interface ButtonProps {
  /** Button text content */
  children: React.ReactNode;
  /** Click handler function */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Button variant for styling */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Whether button takes full width */
  fullWidth?: boolean;
  /** Optional icon component */
  icon?: LucideIcon;
  /** Button type attribute */
  type?: 'button' | 'submit' | 'reset';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Enhanced Button Component with modern design system
 * Features gradients, animations, and glassmorphism effects
 */
const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  icon: Icon,
  type = 'button',
  className = ''
}) => {
  // Base button classes with modern interactive features
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'font-medium',
    'rounded-lg',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'relative',
    'overflow-hidden',
    'group',
    'transform',
    'transition-all',
    'duration-200',
    'ease-out'
  ];

  // Size-specific classes with consistent heights
  const sizeClasses = {
    small: ['px-3', 'py-2', 'text-sm', 'h-9'],
    medium: ['px-4', 'py-3', 'text-base', 'h-11'],
    large: ['px-6', 'py-4', 'text-lg', 'font-semibold', 'h-13']
  };

  // Variant-specific styles using design system tokens
  const getVariantStyles = (variant: ButtonVariant) => {
    const baseStyles = {
      transition: 'var(--transition-all)',
      transform: 'translateY(0)',
      borderRadius: 'var(--radius-lg)',
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          background: componentThemes.button.primary.gradient,
          color: componentThemes.button.primary.color,
          boxShadow: componentThemes.button.primary.shadow,
        };
      case 'secondary':
        return {
          ...baseStyles,
          background: componentThemes.button.secondary.background,
          color: componentThemes.button.secondary.color,
          boxShadow: componentThemes.button.secondary.shadow,
          border: '1px solid var(--border-primary)',
        };
      case 'danger':
        return {
          ...baseStyles,
          background: componentThemes.button.danger.gradient,
          color: componentThemes.button.danger.color,
          boxShadow: componentThemes.button.danger.shadow,
        };
      case 'success':
        return {
          ...baseStyles,
          background: componentThemes.button.success.gradient,
          color: componentThemes.button.success.color,
          boxShadow: componentThemes.button.success.shadow,
        };
      case 'glass':
        return {
          ...baseStyles,
          background: componentThemes.button.glass.background,
          backdropFilter: componentThemes.button.glass.backdropFilter,
          border: componentThemes.button.glass.border,
          boxShadow: componentThemes.button.glass.shadow,
          color: 'var(--text-primary)',
        };
      default:
        return baseStyles;
    }
  };

  // Interactive hover and active states with micro-animations
  const variantClasses = {
    primary: ['hover:scale-105', 'hover:-translate-y-0.5', 'active:scale-100', 'active:translate-y-0'],
    secondary: ['hover:scale-105', 'hover:-translate-y-0.5', 'active:scale-100', 'active:translate-y-0'],
    danger: ['hover:scale-105', 'hover:-translate-y-0.5', 'active:scale-100', 'active:translate-y-0'],
    success: ['hover:scale-105', 'hover:-translate-y-0.5', 'active:scale-100', 'active:translate-y-0'],
    glass: ['hover:scale-105', 'hover:-translate-y-0.5', 'active:scale-100', 'active:translate-y-0']
  };

  // Width classes
  const widthClasses = fullWidth ? ['w-full'] : [];

  // Combine all classes
  const buttonClasses = [
    ...baseClasses,
    ...sizeClasses[size],
    ...variantClasses[variant],
    ...widthClasses,
    className
  ].join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      style={getVariantStyles(variant)}
      onClick={onClick}
      disabled={disabled}
    >
      {/* Shimmer effect overlay for premium feel */}
      <div className="absolute inset-0 -top-0.5 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out" />
      
      {/* Button content with proper z-index */}
      <div className="relative z-10 flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5" />}
        {children}
      </div>
    </button>
  );
};

export default Button;